/**
 * Centralized color definitions for table columns
 *
 * These color names map to chipBaseColors in components/chip/Chip.tsx
 * which in turn maps to theme colors from styles/theme/colors.ts
 *
 * Available colors:
 * - brand: Teal/green (primary brand color)
 * - coral: Coral/salmon
 * - grey: Neutral grey
 * - lavender: Purple/lavender
 * - red: Error/danger red
 * - yellow: Warning yellow
 * - blue: Marine blue
 * - white: White with grey border
 * - green: Success green
 */

// Named colors that map to chipBaseColors
export const ColumnColor = {
  BRAND: 'brand',
  CORAL: 'coral',
  GREY: 'grey',
  LAVENDER: 'lavender',
  RED: 'red',
  YELLOW: 'yellow',
  BLUE: 'blue',
  WHITE: 'white',
  GREEN: 'green',
} as const;

export type ColumnColorType = (typeof ColumnColor)[keyof typeof ColumnColor];

// Semantic color mappings for common use cases
export const SemanticColors = {
  // Boolean/Status states
  active: ColumnColor.GREEN,
  inactive: ColumnColor.GREY,

  // Default colors for tags and chips
  default: ColumnColor.BRAND,
  defaultTag: ColumnColor.WHITE,
  defaultChip: ColumnColor.BRAND,

  // Status indicators
  success: ColumnColor.GREEN,
  warning: ColumnColor.YELLOW,
  error: ColumnColor.RED,
  info: ColumnColor.BLUE,
} as const;

// Boolean field colors (Yes/No, True/False)
export const BooleanColors = {
  true: ColumnColor.GREEN,
  false: ColumnColor.GREY,
} as const;

// Status field colors (Active/Inactive)
export const StatusColors = {
  true: ColumnColor.GREEN,
  false: ColumnColor.GREY,
} as const;
